import { useState, forwardRef, useLayoutEffect } from 'react'

import useCurrentLocationContext from '@contexts/currentLocationContext'
import { isAvatarSrc, thumbnailMediumBlue } from '@utils/thumbnails'
import useScrollTriggerContext from '@contexts/scrollTriggerContext'
import { isOnlineOrRecent } from '@utils/isOnlineOrRecent'
import useMapDataContext, { MapData } from '@contexts/mapDataContext'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import useLocalState from '@hooks/useLocalState'
import { useNavigate } from 'react-router-dom'
import { colors, size } from '@styles/theme'
import useLoading from '@hooks/useLoading'
import useScroll from '@hooks/useScroll'
import axios from '@utils/webRequests'
import styled from '@emotion/styled'

import LocationPermissionDenied from '@components/layout/screen_loaders/LocationPermissionDenied'
import HiddenDisplay from '@components/nearby/HiddenDisplay'
import NearbyTopNav from '@components/nearby/NearbyTopNav'
import { SentOrReply } from '@elements/icons/SentOrReply'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { StatusIcon } from '@elements/StatusIndicator'
import AnimationImage from '@elements/AnimationImage'
import { EndOfGrid } from '@elements/EmptyState'
import { VirtuosoGrid } from 'react-virtuoso'

const GridContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;

  height: 100dvh;
  width: 100dvw;
  
  overflow-y: scroll;
`

const GridItem = styled.div`
  width: calc(100dvw/3);
  height: calc(100dvw/2.9);
  flex-basis: calc(100dvw/3);
  background-color: #141414;
  border-bottom: 1px solid #141414;
  
  // left pictures
  &:nth-of-type(3n - 2) {
    border-left: 1px solid #141414;
  }

  // center pictures
  &:nth-of-type(3n - 1) {
    border-left: 1px solid #141414;
    border-right: 1px solid #141414;
  }

  // right pictures 
  &:nth-of-type(3n) {
    border-right: 1px solid #141414;
  }
`

// Ensure that this stays out of the component, 
// Otherwise the grid will remount with each render due to new component instances.
const gridComponents = {
  List: forwardRef(({ style, children, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      style={{
        display: "flex",
        flexWrap: "wrap",
        borderRadius: '20px',
        overflow: 'hidden',
        borderTop: '5px solid #141414',
        ...style,
      }}
    >
      {children}
    </div>
  )),
  Item: ({ children, ...props }) => (
    <GridItem {...props}>
      {children}
    </GridItem>
  ),
  Header: () => (
    // Just a clearfix
    <div style={{ height: 106, backgroundColor: 'black', }} />
  ),
  Footer: ({ context }) => !context?.isUsersLoading && <EndOfGrid />,
}

const UserComponent = ({ user, navigateFunc }) => {
  const { state: authState } = useAuthContext()
  const isAvatar = isAvatarSrc('medium', user?.nearbyMedia?.thumbnails?.[0], user?.defaultAvatar)
  const userThumbnail = thumbnailMediumBlue(user?.nearbyMedia?.thumbnails?.[0], user?.defaultAvatar)
  const { isOnline, isRecent } = isOnlineOrRecent(user)
  const isOwnProfile = user?.nearbyUuid === authState?.user?.nearbyUuid
  const mapUserData = MapData.getProfile(user?.nearbyUuid)
  const hasLastMessage = mapUserData?.lastMessage
  const isNewReply = hasLastMessage && mapUserData.lastMessage?.nearbyUuid === mapUserData.nearbyUuid && mapUserData?.lastMessage?.isNew

  return (
    <div
      onClick={() => navigateFunc({ pathname: '/grid', search: { nearby_uuid: user?.nearbyUuid } }, { state: { user: JSON.stringify(user) } })}
      style={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden', flexShrink: 0, borderRadius: '3px', border: isNewReply ? `1.5px solid ${colors.onlineIndicator()}` : 'none' }}>
      {user.tempStatus && user.tempStatus !== 'browsing' &&
        <StatusIcon
          status={user.tempStatus}
          style={{ position: 'absolute', top: size(0.5), left: size(0.5), height: size(2.25), width: size(2.5), zIndex: 2 }}
        />
        // <StatusIndicator
        //   status={user.tempStatus}
        //   containerStyles={{ position: 'absolute', top: size(0.5), left: size(0.5), height: size(2.25), width: size(2.5), zIndex: 2 }}
        // />
      }
      {
        <div style={{ display: 'flex', alignItems: 'center', gap: size(0.5), position: 'absolute', bottom: 0, left: 0, zIndex: 2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '100%', height: size(2.15), fontSize: '0.7rem', paddingLeft: size(0.5), paddingBottom: size(0.25), width: `calc(100dvw/3 - ${hasLastMessage ? size(3.5) : size(1)})`, overflow: 'hidden', color: 'lightgrey', fontWeight: '500' }}>
          {(isOnline || isRecent || isOwnProfile) && <div style={{ height: size(1.15), width: size(1.15), marginBottom: size(0.15), marginLeft: size(0.15), borderRadius: 100, flexShrink: 0, backgroundColor: (isOwnProfile || isOnline) ? colors.onlineIndicator() : (isRecent ? colors.recentIndicator() : 'transparent') }} />}
          {user?.extendedInfos?.tagline || ((isOwnProfile || isOnline) ? 'online' : (isRecent ? 'recent' : ''))}
        </div>
      }
      <SentOrReply
        mapUserData={mapUserData}
        size={size(2)}
        style={{ position: 'absolute', bottom: size(0.5), right: size(0.5), zIndex: 10 }}
      />
      <AnimationImage
        src={userThumbnail}
        style={{ height: '100%', width: '100%', filter: isAvatar && 'grayscale(100%) contrast(120%) brightness(40%)' }}
      />
      <div style={{ height: '100%', width: '100%', background: 'linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(255,255,255,0) 50%)', zIndex: 1, position: 'absolute', top: 0, left: 0 }} />
    </div>
  )
}

const NearbyGrid = () => {
  const { state: authState } = useAuthContext()
  const { currentLocation, currentLocationRef, currentLocationExists, isLocationPermissionDenied } = useCurrentLocationContext()
  const { profilesInitialized } = useMapDataContext()
  const [users, setUsers] = useLocalState('nearby-grid-users', [])
  const { isLoading: isUsersLoading, setIsLoading: setIsUsersLoading } = useLoading(true)
  const [scrollTarget, setScrollTarget] = useState(undefined)
  const { setScrollRef, scrollRef } = useScroll(setScrollTarget, 500)
  const { showHiddenDisplay } = useAuthContext()
  // // Global Scroll Trigger
  const { useScrollTriggerEffect } = useScrollTriggerContext()
  useScrollTriggerEffect(scrollTarget)
  const { nearbyProfile } = useModalContext()
  const navigate = useNavigate()

  // navigates back to map screen if the profiles have yet to be initialized
  useLayoutEffect(() => {
    if (!profilesInitialized) navigate('/map')
  }, [profilesInitialized])

  // grid initialization and user retreival
  useLayoutEffect(() => {
    if (currentLocationExists) {
      getUsers()
  
      const interval = setInterval(() => {
        getUsers()
      }, 120_000)
  
      return () => clearInterval(interval)
    }
  }, [currentLocationExists])

  const getUsers = () => {
    const [lon, lat] = currentLocationRef.current || []
    // Validate that the current location hasn't changed since this was last called in the interval
    if (!lon || !lat) return;
    return axios.get(`/api/users/nearby_profiles?radius=true&lonlat=[${lon},${lat}]`)
      .then(res => {
        const nearbyUsers = res.data
        if (authState?.user) nearbyUsers.unshift(authState.user)
        setUsers(nearbyUsers)
        setIsUsersLoading(false)
      })
      .catch(console.log)
  }

  return(
    <>
      <NearbyTopNav />
      {showHiddenDisplay && <HiddenDisplay />}
      {isLocationPermissionDenied &&
        <GridContainer>
          <LocationPermissionDenied />
        </GridContainer>
      }
      {!showHiddenDisplay && !isLocationPermissionDenied && (
        <GridContainer>
          <PullToRefresh
            onRefresh={() => getUsers(currentLocation)}
            pullingContent={''}
            isPullable>
            <VirtuosoGrid
              scrollerRef={node => { if (node) setScrollRef(node) }}
              style={{ height: '100dvh' }}
              data={users}
              overscan={window.screen.height*2}
              context={{ isUsersLoading }}
              // atBottomThreshold={window.innerHeight*3}
              // endReached={!noMorePosts && getMorePosts}
              // { ...restoreStateProp }
              components={gridComponents}
              itemContent={(index) => (
                <UserComponent
                  navigateFunc={nearbyProfile.open}
                  user={users[index]}
                  key={index}
                />
              )}
            />
          </PullToRefresh>
        </GridContainer>
      )}
    </>
  )
}

export default NearbyGrid
